import React, { useState } from "react";
import {
  Container,
  Paper,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Alert,
  Stack,
  Group,
  Box,
  Code,
  CopyButton,
  ActionIcon,
  Tooltip,
  ThemeIcon,
  Center,
  SimpleGrid,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconShieldLock,
  IconCopy,
  IconCheck,
  IconArrowLeft,
  IconKey,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "axiosInstance";
import { setupUserSession } from "authProvider";

type Step = "login" | "mfa_enroll" | "mfa_challenge" | "recovery_codes";

interface MfaPayload {
  mfa_required: boolean;
  mfa_enrolled: boolean;
  mfa_pending_token: string;
  mfa_secret?: string;
  mfa_qr_svg?: string;
}

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mfaData, setMfaData] = useState<MfaPayload | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_CP_API_URL}/api/admin/users/login`,
        {
          email,
          password,
        },
      );

      const data = response.data?.data;

      if (data?.mfa_required) {
        setMfaData(data);
        setMfaCode("");
        if (!data.mfa_enrolled) {
          setStep("mfa_enroll");
        } else {
          setStep("mfa_challenge");
        }
      } else if (data?.token) {
        await setupUserSession(data.token);
        navigate("/");
      } else {
        setError("Invalid email or password.");
      }
    } catch (err: any) {
      setError(err?.message || "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaCode || !mfaData?.mfa_pending_token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_CP_API_URL}/api/admin/users/mfa_verify`,
        {
          mfa_pending_token: mfaData.mfa_pending_token,
          mfa_code: mfaCode.trim(),
        },
      );

      const data = response.data?.data;

      if (data?.token) {
        await setupUserSession(data.token);
        if (data.recovery_codes && data.recovery_codes.length > 0) {
          setRecoveryCodes(data.recovery_codes);
          setStep("recovery_codes");
        } else {
          navigate("/");
        }
      } else {
        setError("Invalid verification code. Please try again.");
      }
    } catch (err: any) {
      setError(err?.message || "Invalid verification code or session expired.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setStep("login");
    setMfaCode("");
    setMfaData(null);
    setError(null);
  };

  const handleProceedToDashboard = () => {
    navigate("/");
  };

  return (
    <Box
      sx={(theme) => ({
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor:
          theme.colorScheme === "dark"
            ? theme.colors.dark[8]
            : theme.colors.gray[0],
        padding: theme.spacing.md,
      })}
    >
      <Container size={step === "mfa_enroll" || step === "recovery_codes" ? 520 : 420}>
        <Paper
          withBorder
          shadow="md"
          p={30}
          radius="md"
          sx={(theme) => ({
            backgroundColor:
              theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
          })}
        >
          <Center mb="lg">
            <ThemeIcon size={56} radius="xl" variant="light" color="blue">
              <IconShieldLock size={32} />
            </ThemeIcon>
          </Center>

          {error && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Authentication Notice"
              color="red"
              mb="md"
              radius="md"
            >
              {error}
            </Alert>
          )}

          {/* STEP 1: INITIAL LOGIN */}
          {step === "login" && (
            <form onSubmit={handleLoginSubmit}>
              <Stack spacing="md">
                <Box sx={{ textAlign: "center" }}>
                  <Title order={2} weight={700}>
                    Ichigo Admin
                  </Title>
                  <Text color="dimmed" size="sm" mt={4}>
                    Sign in with your admin credentials
                  </Text>
                </Box>

                <TextInput
                  label="Email"
                  placeholder="admin@ichigo.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  autoComplete="email"
                  autoFocus
                />

                <PasswordInput
                  label="Password"
                  placeholder="Your password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  autoComplete="current-password"
                />

                <Button type="submit" fullWidth mt="xs" loading={loading}>
                  Sign In
                </Button>
              </Stack>
            </form>
          )}

          {/* STEP 2: MFA ENROLLMENT */}
          {step === "mfa_enroll" && mfaData && (
            <form onSubmit={handleMfaVerify}>
              <Stack spacing="md">
                <Box sx={{ textAlign: "center" }}>
                  <Title order={3} weight={700}>
                    Set Up Two-Factor Authentication
                  </Title>
                  <Text color="dimmed" size="sm" mt={4}>
                    Scan the QR code with Google Authenticator, 1Password, or Authy
                  </Text>
                </Box>

                {mfaData.mfa_qr_svg && (
                  <Center my="xs">
                    <Box
                      p="xs"
                      sx={{
                        backgroundColor: "white",
                        borderRadius: 8,
                        border: "1px solid #e0e0e0",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 196,
                        height: 196,
                        "& svg": {
                          display: "block",
                          width: "100%",
                          height: "100%",
                          maxWidth: 180,
                          maxHeight: 180,
                        },
                      }}
                      dangerouslySetInnerHTML={{
                        __html: mfaData.mfa_qr_svg.replace(/<\?xml[^>]*\?>/gi, "").trim(),
                      }}
                    />
                  </Center>
                )}

                {mfaData.mfa_secret && (
                  <Box>
                    <Text size="xs" color="dimmed" mb={4}>
                      Or enter this key manually:
                    </Text>
                    <Group spacing="xs" position="apart" noWrap>
                      <Code
                        sx={{
                          fontSize: "0.85rem",
                          letterSpacing: "1.5px",
                          fontWeight: 600,
                          flex: 1,
                          textAlign: "center",
                          padding: "6px 10px",
                        }}
                      >
                        {mfaData.mfa_secret}
                      </Code>
                      <CopyButton value={mfaData.mfa_secret} timeout={2000}>
                        {({ copied, copy }) => (
                          <Tooltip
                            label={copied ? "Copied" : "Copy Secret"}
                            withArrow
                            position="right"
                          >
                            <ActionIcon
                              color={copied ? "teal" : "gray"}
                              onClick={copy}
                              variant="light"
                            >
                              {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                    </Group>
                  </Box>
                )}

                <TextInput
                  label="6-Digit Verification Code"
                  placeholder="123456"
                  required
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.currentTarget.value)}
                  autoFocus
                  maxLength={6}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                />

                <Button type="submit" fullWidth loading={loading}>
                  Verify and Activate MFA
                </Button>

                <Button
                  variant="subtle"
                  color="gray"
                  size="xs"
                  leftIcon={<IconArrowLeft size={14} />}
                  onClick={handleBackToLogin}
                >
                  Back to Sign In
                </Button>
              </Stack>
            </form>
          )}

          {/* STEP 3: MFA CHALLENGE */}
          {step === "mfa_challenge" && (
            <form onSubmit={handleMfaVerify}>
              <Stack spacing="md">
                <Box sx={{ textAlign: "center" }}>
                  <Title order={3} weight={700}>
                    Two-Factor Authentication
                  </Title>
                  <Text color="dimmed" size="sm" mt={4}>
                    Enter the 6-digit code from your authenticator app or an 8-character recovery code
                  </Text>
                </Box>

                <TextInput
                  label="Security Code"
                  placeholder="123456 or recovery code"
                  required
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.currentTarget.value)}
                  autoFocus
                  autoComplete="one-time-code"
                  inputMode="numeric"
                />

                <Button type="submit" fullWidth loading={loading}>
                  Verify
                </Button>

                <Button
                  variant="subtle"
                  color="gray"
                  size="xs"
                  leftIcon={<IconArrowLeft size={14} />}
                  onClick={handleBackToLogin}
                >
                  Back to Sign In
                </Button>
              </Stack>
            </form>
          )}

          {/* STEP 4: RECOVERY CODES DISPLAY */}
          {step === "recovery_codes" && (
            <Stack spacing="md">
              <Box sx={{ textAlign: "center" }}>
                <ThemeIcon size={44} radius="xl" color="green" variant="light" mb="xs">
                  <IconKey size={24} />
                </ThemeIcon>
                <Title order={3} weight={700}>
                  Save Your Recovery Codes
                </Title>
                <Text color="dimmed" size="sm" mt={4}>
                  Store these backup recovery codes safely. Each code can be used once if you lose access to your authenticator device.
                </Text>
              </Box>

              <SimpleGrid cols={2} spacing="xs">
                {recoveryCodes.map((code, index) => (
                  <Code
                    key={index}
                    sx={{
                      textAlign: "center",
                      fontWeight: 700,
                      letterSpacing: "1.5px",
                      padding: "8px",
                    }}
                  >
                    {code}
                  </Code>
                ))}
              </SimpleGrid>

              <CopyButton value={recoveryCodes.join("\n")} timeout={2000}>
                {({ copied, copy }) => (
                  <Button
                    variant="outline"
                    color={copied ? "teal" : "blue"}
                    leftIcon={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    onClick={copy}
                    fullWidth
                  >
                    {copied ? "Codes Copied to Clipboard" : "Copy All Recovery Codes"}
                  </Button>
                )}
              </CopyButton>

              <Button fullWidth onClick={handleProceedToDashboard} color="green">
                I Have Saved My Codes - Go to Dashboard
              </Button>
            </Stack>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;
